import { FormEvent } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import ErrorAlert from '@/shared/components/ErrorAlert';
import FormField from '@/shared/components/FormField';
import FormInput from '@/shared/components/FormInput';
import OAuthButtons from '../oauth/OAuthButtons';
import OAuthDivider from '../oauth/OAuthDivider';
import CookiesDisabledAlert from './CookiesDisabledAlert';
import AuthFormHeading from '../common/components/AuthFormHeading';

interface Props {
  email: string;
  password: string;
  error: string | null;
  oauthError: string | null;
  isLoading: boolean;
  onEmailChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  onSubmit: (event: FormEvent) => void;
  onOAuth: (provider: 'google_oauth2' | 'microsoft_graph' | 'facebook') => void;
  onSwitchToSignUp: () => void;
  onForgotPassword: () => void;
}

export default function SignInForm({
  email,
  password,
  error,
  oauthError,
  isLoading,
  onEmailChange,
  onPasswordChange,
  onSubmit,
  onOAuth,
  onSwitchToSignUp,
  onForgotPassword,
}: Props) {
  const intl = useIntl();
  const isCookiesDisabled = !navigator.cookieEnabled;

  return (
    <div className="space-y-6">
      <AuthFormHeading headingId="auth.signIn.heading" descriptionId="auth.signIn.description" />

      {isCookiesDisabled && <CookiesDisabledAlert />}
      {error && <ErrorAlert message={error} />}

      <OAuthButtons onOAuth={onOAuth} error={oauthError} />

      <OAuthDivider />

      <form className="space-y-5" onSubmit={onSubmit}>
        <FormField id="email" labelId="auth.signIn.emailLabel">
          <FormInput
            id="email"
            type="email"
            autoComplete="email"
            required
            placeholder={intl.formatMessage({ id: 'auth.signIn.emailPlaceholder' })}
            value={email}
            onChange={(e) => onEmailChange(e.target.value)}
          />
        </FormField>

        <FormField id="password" labelId="auth.signIn.passwordLabel">
          <FormInput
            id="password"
            type="password"
            autoComplete="current-password"
            required
            placeholder={intl.formatMessage({ id: 'auth.signIn.passwordPlaceholder' })}
            value={password}
            onChange={(e) => onPasswordChange(e.target.value)}
          />
        </FormField>

        <div className="text-right">
          <button
            type="button"
            onClick={onForgotPassword}
            className="text-sm font-medium text-brand-600 hover:text-brand-500"
          >
            <FormattedMessage id="auth.signIn.forgotPassword" />
          </button>
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="inline-flex w-full items-center justify-center rounded-lg bg-brand-700 px-4 py-3 text-sm font-semibold text-white transition hover:bg-brand-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-300 disabled:opacity-60"
        >
          {isLoading ? (
            <FormattedMessage id="auth.signIn.submitting" />
          ) : (
            <FormattedMessage id="auth.signIn.submit" />
          )}
        </button>
      </form>

      <div className="text-center">
        <button
          type="button"
          onClick={onSwitchToSignUp}
          className="text-sm font-semibold text-brand-700 hover:text-brand-600"
        >
          <FormattedMessage id="auth.signIn.switchToSignUp" />
        </button>
      </div>
    </div>
  );
}
